/**
 * A short, readable preview of a Markdown + LaTeX content string — used by
 * the end-of-session summary list (`practice/SessionSummary.tsx`) and the
 * teacher's question list (`teacher/QuestionsPage.tsx`). Naive slicing of
 * the raw source cuts mid-command and leaves `$`/`\` on screen, so this
 * strips math and Markdown syntax first and only then truncates, always on
 * a word boundary.
 */

const DISPLAY_MATH = /\$\$[\s\S]*?\$\$/g;
const INLINE_MATH = /\$[^$\n]+?\$/g;
/** Stands in for a removed expression — never a `$` or a backslash command. */
const MATH_PLACEHOLDER = "…";

const MARKDOWN_PATTERNS: Array<[RegExp, string]> = [
  [/!\[[^\]]*\]\([^)]*\)/g, ""], // images
  [/\[([^\]]*)\]\([^)]*\)/g, "$1"], // links -> label text
  [/(\*\*|__)(.*?)\1/g, "$2"], // bold
  [/(\*|_)(.*?)\1/g, "$2"], // italic
  [/`([^`]*)`/g, "$1"], // inline code
  [/^#{1,6}\s+/gm, ""], // headings
  [/^>\s?/gm, ""], // blockquotes
  [/^[-*+]\s+/gm, ""], // bullet list markers
  [/^\d+\.\s+/gm, ""], // ordered list markers
];

function stripMarkdown(text: string): string {
  return MARKDOWN_PATTERNS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    text,
  );
}

function truncateAtWordBoundary(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const slice = text.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = (lastSpace > 0 ? slice.slice(0, lastSpace) : slice).trimEnd();
  return `${cut}…`;
}

/**
 * Removes display-math blocks entirely, replaces inline math with a compact
 * placeholder, drops Markdown syntax, then truncates on a word boundary —
 * so a `$…$` span is never cut mid-way (it is gone before truncation runs).
 */
export function previewStatement(source: string, maxLength = 100): string {
  const withoutMath = source
    .replace(DISPLAY_MATH, " ") // display math is a worked step, not part of the preview
    .replace(INLINE_MATH, ` ${MATH_PLACEHOLDER} `);
  const plain = stripMarkdown(withoutMath).replace(/\s+/g, " ").trim();
  return truncateAtWordBoundary(plain, maxLength);
}
